import axios from 'axios';
import { getAuthToken } from './authStorage';
import { API_BASE_URL } from './apiBaseUrl';

export interface ClientDTO {
  id?: string;
  firstName: string;
  lastName: string;
  company?: string;
  phone?: string;
  email?: string;
  address: string;
  postalCode?: string;
  city?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
  notes?: string;
  tags?: string[];
  defaultVisitDuration?: number;
  isActive?: boolean;
  availabilityHours?: Record<string, any>;
  createdAt?: string;
  updatedAt?: string;
}

export interface ClientFilters {
  page?: number;
  limit?: number;
  search?: string;
  active?: boolean;
  tags?: string[];
}

export interface AddressValidationRequest {
  address?: string;
  postalCode?: string;
  city?: string;
  country?: string;
}

export interface AddressSuggestion {
  label: string;
  address: string;
  postalCode: string;
  city: string;
  country: string;
  latitude: number;
  longitude: number;
  score: number;
}

export interface AddressValidationResult {
  isValid: boolean;
  confidence: number;
  warning?: string;
  suggestions: AddressSuggestion[];
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  meta?: {
    total: number;
    page: number;
    limit: number;
  };
  message?: string;
}

const clientApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur pour logger les erreurs
clientApi.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

clientApi.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

export const clientService = {
  // Récupérer tous les clients avec filtres
  async getClients(filters?: ClientFilters): Promise<ApiResponse<ClientDTO[]>> {
    const params = new URLSearchParams();
    
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.search) params.append('search', filters.search);
    if (filters?.active !== undefined) params.append('active', filters.active.toString());
    if (filters?.tags && filters.tags.length > 0) params.append('tags', filters.tags.join(','));

    const response = await clientApi.get<ApiResponse<ClientDTO[]>>(`/clients?${params.toString()}`);
    return response.data;
  },

  // Récupérer un client par ID
  async getClientById(id: string): Promise<ApiResponse<ClientDTO>> {
    const response = await clientApi.get<ApiResponse<ClientDTO>>(`/clients/${id}`);
    return response.data;
  },

  // Créer un nouveau client
  async createClient(client: Omit<ClientDTO, 'id' | 'createdAt' | 'updatedAt'>): Promise<ApiResponse<ClientDTO>> {
    const response = await clientApi.post<ApiResponse<ClientDTO>>('/clients', client);
    return response.data;
  },

  // Mettre à jour un client
  async updateClient(id: string, client: Partial<ClientDTO>): Promise<ApiResponse<ClientDTO>> {
    const response = await clientApi.put<ApiResponse<ClientDTO>>(`/clients/${id}`, client);
    return response.data;
  },

  // Supprimer un client
  async deleteClient(id: string): Promise<ApiResponse<void>> {
    const response = await clientApi.delete<ApiResponse<void>>(`/clients/${id}`);
    return response.data;
  },

  // Rechercher des clients
  async searchClients(query: string): Promise<ApiResponse<ClientDTO[]>> {
    const response = await clientApi.get<ApiResponse<ClientDTO[]>>(`/clients/search?q=${encodeURIComponent(query)}`);
    return response.data;
  },

  // Vérifier une adresse (non bloquant)
  async validateAddress(payload: AddressValidationRequest): Promise<ApiResponse<AddressValidationResult>> {
    const response = await clientApi.post<ApiResponse<AddressValidationResult>>('/clients/address/validate', payload);
    return response.data;
  },
};

export default clientService;
